/**
 * CreateLayerDialog Component
 *
 * Dialog for creating a new empty vector layer with:
 * - Layer name input
 * - Geometry type selection (Point, LineString, Polygon)
 * - CRS selection (EPSG:4326, EPSG:3857, UTM zones)
 * - Attribute field definition (name, type, default value)
 *
 * Validates: Requirements 47.1, 47.2, 47.3, 47.4, 47.5
 */
import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Typography,
  Box,
  Divider,
  Alert,
  Chip,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import type {
  LayerGeometryType,
  AttributeField,
  AttributeFieldType,
  CreateLayerConfig,
} from '../types/layerCreation';
import { SUPPORTED_CRS, getDefaultAttributeFields } from '../types/layerCreation';
import { layerCreatorService } from '../services/LayerCreatorService';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CreateLayerDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (config: CreateLayerConfig) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const CreateLayerDialog: React.FC<CreateLayerDialogProps> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const [name, setName] = useState('');
  const [geometryType, setGeometryType] = useState<LayerGeometryType>('Point');
  const [crs, setCrs] = useState('EPSG:4326');
  const [attributeFields, setAttributeFields] = useState<AttributeField[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  // Automatically populate default fields when geometry type changes
  useEffect(() => {
    if (open) {
      setAttributeFields(getDefaultAttributeFields(geometryType));
    }
  }, [geometryType, open]);

  // New field form state
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldType, setNewFieldType] = useState<AttributeFieldType>('string');
  const [newFieldDefault, setNewFieldDefault] = useState('');

  const resetForm = useCallback(() => {
    setName('');
    setGeometryType('Point');
    setCrs('EPSG:4326');
    setAttributeFields(getDefaultAttributeFields('Point'));
    setErrors([]);
    setNewFieldName('');
    setNewFieldType('string');
    setNewFieldDefault('');
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleAddField = useCallback(() => {
    if (!newFieldName.trim()) return;

    // Check for duplicate names
    if (attributeFields.some((f) => f.name === newFieldName.trim())) {
      setErrors(['Attribute field name already exists']);
      return;
    }

    const field: AttributeField = {
      name: newFieldName.trim(),
      type: newFieldType,
    };

    // Parse default value based on type
    if (newFieldDefault.trim()) {
      switch (newFieldType) {
        case 'number':
          field.defaultValue = Number(newFieldDefault) || 0;
          break;
        case 'boolean':
          field.defaultValue = newFieldDefault.toLowerCase() === 'true';
          break;
        default:
          field.defaultValue = newFieldDefault;
      }
    }

    setAttributeFields([...attributeFields, field]);
    setNewFieldName('');
    setNewFieldDefault('');
    setErrors([]);
  }, [newFieldName, newFieldType, newFieldDefault, attributeFields]);

  const handleRemoveField = useCallback(
    (index: number) => {
      setAttributeFields(attributeFields.filter((_, i) => i !== index));
    },
    [attributeFields]
  );

  const handleConfirm = useCallback(() => {
    const config: CreateLayerConfig = {
      name: name.trim(),
      geometryType,
      crs,
      attributeFields,
    };

    const validation = layerCreatorService.validateCreateLayerConfig(config);
    if (!validation.valid) {
      setErrors(validation.errors);
      return;
    }

    onConfirm(config);
    resetForm();
  }, [name, geometryType, crs, attributeFields, onConfirm, resetForm]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Layer</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {errors.length > 0 && (
            <Alert severity="error">
              {errors.map((err, i) => (
                <div key={i}>{err}</div>
              ))}
            </Alert>
          )}

          {/* Layer Name */}
          <TextField
            label="Layer Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            autoFocus
            placeholder="Enter layer name"
          />

          {/* Geometry Type Selection */}
          <FormControl fullWidth required>
            <InputLabel id="geometry-type-label">Geometry Type</InputLabel>
            <Select
              labelId="geometry-type-label"
              value={geometryType}
              label="Geometry Type"
              onChange={(e) => setGeometryType(e.target.value as LayerGeometryType)}
            >
              <MenuItem value="Point">Point</MenuItem>
              <MenuItem value="LineString">LineString</MenuItem>
              <MenuItem value="Polygon">Polygon</MenuItem>
            </Select>
          </FormControl>

          {/* CRS Selection */}
          <FormControl fullWidth required>
            <InputLabel id="crs-label">Coordinate Reference System</InputLabel>
            <Select
              labelId="crs-label"
              value={crs}
              label="Coordinate Reference System"
              onChange={(e) => setCrs(e.target.value)}
            >
              {SUPPORTED_CRS.map((option) => (
                <MenuItem key={option.code} value={option.code}>
                  {option.code} - {option.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider />

          {/* Attribute Fields */}
          <Typography variant="subtitle1" fontWeight="bold">
            Attribute Fields
          </Typography>

          {attributeFields.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {attributeFields.map((field, index) => (
                <Chip
                  key={index}
                  label={`${field.name} (${field.type})`}
                  onDelete={() => handleRemoveField(index)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          )}

          {/* Add new field form */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              label="Field Name"
              value={newFieldName}
              onChange={(e) => setNewFieldName(e.target.value)}
              size="small"
              sx={{ flex: 2 }}
              placeholder="e.g. name, population"
            />
            <FormControl size="small" sx={{ flex: 1, minWidth: 100 }}>
              <InputLabel id="field-type-label">Type</InputLabel>
              <Select
                labelId="field-type-label"
                value={newFieldType}
                label="Type"
                onChange={(e) => setNewFieldType(e.target.value as AttributeFieldType)}
              >
                <MenuItem value="string">String</MenuItem>
                <MenuItem value="number">Number</MenuItem>
                <MenuItem value="boolean">Boolean</MenuItem>
                <MenuItem value="date">Date</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Default"
              value={newFieldDefault}
              onChange={(e) => setNewFieldDefault(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
              placeholder="Optional"
            />
            <IconButton
              onClick={handleAddField}
              color="primary"
              disabled={!newFieldName.trim()}
              aria-label="Add field"
            >
              <AddIcon />
            </IconButton>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained" disabled={!name.trim()}>
          Create Layer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateLayerDialog;
